import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import {
  inspectionService,
  type EvidenceCandidateItem,
  type EvidenceCandidatesResponse,
} from '@/services/inspection.service';
import type {
  EvidenceSelectionItem,
  EvidenceSourceType,
} from '@/services/authorization-case.service';

interface Props {
  inspectionId: string;
  selectedItems: EvidenceSelectionItem[];
  onChange: (items: EvidenceSelectionItem[]) => void;
  disabled?: boolean;
}

interface CategorySpec {
  key: keyof EvidenceCandidatesResponse;
  label: string;
  sourceType: EvidenceSourceType;
}

// "Attachments" and "Documents" collapse into one DOCUMENT-backed category, and
// "Procedure Photos" is not split out from "Procedure Evidence" — the schema only
// has one ProcedureStepEvidence table, not separate photo/evidence tables (see
// docs/reports/authorization/AUTHORIZATION_EVIDENCE_SELECTION_IMPLEMENTATION).
const CATEGORIES: CategorySpec[] = [
  { key: 'inspectionPhotos', label: 'Inspection Photos', sourceType: 'ATTACHMENT' },
  { key: 'findingPhotos', label: 'Finding Photos', sourceType: 'ATTACHMENT' },
  { key: 'videos', label: 'Videos', sourceType: 'ATTACHMENT' },
  { key: 'documents', label: 'Documents / Attachments', sourceType: 'ATTACHMENT' },
  { key: 'procedureEvidence', label: 'Procedure Evidence', sourceType: 'PROCEDURE_EVIDENCE' },
  { key: 'findings', label: 'Specific Findings', sourceType: 'FINDING' },
  { key: 'notes', label: 'Inspection Notes', sourceType: 'NOTE' },
];

export function EvidenceSelectionPicker({
  inspectionId,
  selectedItems,
  onChange,
  disabled,
}: Props) {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['authorization-evidence-candidates', inspectionId],
    queryFn: () => inspectionService.getAuthorizationEvidenceCandidates(inspectionId),
  });

  const isSelected = (sourceType: EvidenceSourceType, sourceId: string) =>
    selectedItems.some((i) => i.sourceType === sourceType && i.sourceId === sourceId);

  const toggle = (sourceType: EvidenceSourceType, sourceId: string) => {
    if (isSelected(sourceType, sourceId)) {
      onChange(
        selectedItems.filter((i) => !(i.sourceType === sourceType && i.sourceId === sourceId)),
      );
    } else {
      onChange([...selectedItems, { sourceType, sourceId }]);
    }
  };

  if (isLoading) {
    return <div className="h-24 animate-pulse rounded-lg bg-muted" />;
  }

  if (isError) {
    return (
      <div className="flex items-center gap-2 text-sm">
        <span className="text-destructive">Failed to load available evidence.</span>
        <button type="button" onClick={() => void refetch()} className="text-primary underline">
          Retry
        </button>
      </div>
    );
  }

  const categoriesWithItems = CATEGORIES.map((c) => ({
    ...c,
    items: (data?.[c.key] ?? []) as EvidenceCandidateItem[],
  })).filter((c) => c.items.length > 0);

  return (
    <div className="space-y-3">
      <div>
        <label className="mb-1 block text-xs font-medium text-foreground">
          Supporting Evidence
        </label>
        <p className="text-xs text-muted-foreground">
          Select the existing inspection data that supports this request. Only what you select here
          will be visible on the Authorization Case — nothing is uploaded again.
        </p>
      </div>

      {categoriesWithItems.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No photos, videos, documents, findings, or notes exist on this inspection yet.
        </p>
      ) : (
        <div className="space-y-3">
          {categoriesWithItems.map((category) => (
            <div key={category.key}>
              <p className="mb-1 text-xs font-medium text-muted-foreground">{category.label}</p>
              <div className="max-h-40 space-y-0.5 overflow-y-auto rounded-lg border border-input bg-card p-2">
                {category.items.map((item) => (
                  <label
                    key={item.id}
                    className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 hover:bg-muted/40"
                  >
                    <input
                      type="checkbox"
                      checked={isSelected(category.sourceType, item.id)}
                      onChange={() => toggle(category.sourceType, item.id)}
                      disabled={disabled}
                      className="rounded accent-primary"
                    />
                    {item.thumbnailUrl && (
                      <img
                        src={item.thumbnailUrl}
                        alt=""
                        className="h-8 w-8 flex-shrink-0 rounded object-cover"
                      />
                    )}
                    <span className="truncate text-sm text-foreground">{item.label}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedItems.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-medium text-muted-foreground">
            {selectedItems.length} item{selectedItems.length === 1 ? '' : 's'} selected
          </p>
          <div className="flex flex-wrap gap-1">
            {selectedItems.map((sel) => {
              const category = CATEGORIES.find((c) => c.sourceType === sel.sourceType);
              const item = category
                ? ((data?.[category.key] ?? []) as EvidenceCandidateItem[]).find(
                    (i) => i.id === sel.sourceId,
                  )
                : undefined;
              return (
                <span
                  key={`${sel.sourceType}:${sel.sourceId}`}
                  className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary"
                >
                  {item?.label ?? sel.sourceId}
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => toggle(sel.sourceType, sel.sourceId)}
                      className="hover:text-primary/70"
                    >
                      <FontAwesomeIcon icon={faXmark} className="h-2.5 w-2.5" />
                    </button>
                  )}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
